import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, ExternalLink } from 'lucide-react';
import { publicUrl, isLoopback } from '../crm/publicUrl';
import crm, { CAMPAIGN_TYPES, CAMPAIGN_STATUS } from '../crm/api';
import LeadGrid from '../crm/LeadGrid';
import LeadLedger from '../crm/LeadLedger';
import LeadDetail from '../crm/LeadDetail';

const typeOf = (k) => CAMPAIGN_TYPES.find((t) => t.key === k) || CAMPAIGN_TYPES[6];
const statusOf = (k) => CAMPAIGN_STATUS.find((s) => s.key === k) || CAMPAIGN_STATUS[1];
const inr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

export default function CampaignDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [stats, setStats] = useState({});
  const [leads, setLeads] = useState([]);
  const [view, setView] = useState('grid');
  const [openId, setOpenId] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    crm.get(`/campaigns/${id}`).then(({ data }) => { setC(data.campaign); setStats(data.stats || {}); }).catch(() => {});
    crm.get(`/leads?campaign=${id}&limit=100`).then(({ data }) => setLeads(data.leads || [])).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!c) return <div className="pt-10 text-[var(--muted)]">Loading…</div>;
  const t = typeOf(c.type); const s = statusOf(c.status);
  const link = c.landingSlug ? publicUrl(`/${c.landingType === 'profile' ? 'profile' : 'microsite'}/${c.landingSlug}?c=${c.code}`) : '';
  const copy = () => { if (link) { navigator.clipboard.writeText(link).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); } };
  const cpl = c.budget && stats.leads ? Math.round(c.budget / stats.leads) : 0;

  const kpis = [
    { label: 'Leads', value: stats.leads || 0, color: '#0e7490' },
    { label: 'Conversion', value: (stats.conversion || 0) + '%', color: '#7c3aed' },
    { label: 'Pipeline ₹', value: inr(stats.pipelineValue), color: '#0b6e3e', small: true },
    { label: 'Won ₹', value: inr(stats.wonValue), color: '#1a8046', small: true },
  ];

  return (
    <div className="space-y-5 max-w-[1180px]">
      <button onClick={() => nav('/campaigns')} className="text-sm font-bold text-[var(--acc2)] hover:gap-2 flex items-center gap-1.5 transition-all">← All campaigns</button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-[var(--text)] tracking-tight">{c.name}</h1>
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-[11px] font-bold uppercase px-2.5 py-0.5 rounded-full text-white" style={{ background: t.color }}>{t.label}</span>
            <span className="text-[11px] font-bold uppercase px-2.5 py-0.5 rounded-full" style={{ color: s.color, background: s.color + '1a' }}>{s.label}</span>
            {c.channel && <span className="text-[12px] text-[var(--muted)]">· {c.channel}</span>}
            {c.owner && <span className="text-[12px] text-[var(--muted)]">· {c.owner.name}</span>}
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div key={k.label} className="bg-[var(--card)] rounded-3xl border border-[var(--border)] card-lux p-5">
            <div className={`font-extrabold leading-none ${k.small ? 'text-[22px]' : 'text-[30px]'}`} style={{ color: k.color }}>{k.value}</div>
            <div className="text-[13px] font-semibold text-[var(--muted)] mt-2">{k.label}</div>
          </div>
        ))}
      </div>

      {/* extra stats + tracking link */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-[var(--card)] rounded-3xl border border-[var(--border)] card-lux p-6">
          <h3 className="font-extrabold text-[var(--text)] text-[16px] mb-4">Tracking link &amp; QR</h3>
          {isLoopback() && (
            <div className="mb-3 text-[12.5px] rounded-lg p-2.5" style={{ background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' }}>
              <b>This QR points at “localhost” — a phone can't open it.</b> On a phone, localhost means the phone itself.
              Set <code>REACT_APP_PUBLIC_URL</code> in <code>.env</code> to your machine's network address (or your live domain) and restart the app.
            </div>
          )}
          {link ? (
            <div className="flex flex-col sm:flex-row items-start gap-5">
              <div className="p-3 bg-white rounded-xl border border-[var(--border)] shrink-0"><QRCodeSVG value={link} size={116} level="M" /></div>
              <div className="min-w-0 flex-1">
                <div className="text-[12px] text-[var(--muted)] mb-1">Share this link / QR — leads captured through it are auto-tagged to <b>{c.name}</b>.</div>
                <div className="flex items-center gap-2 bg-[var(--soft)] rounded-lg px-3 py-2 mt-1">
                  <span className="font-mono text-[12px] text-[var(--text)] truncate flex-1">{link}</span>
                  <button onClick={copy} className="text-[var(--acc2)] shrink-0" title="Copy"><Copy size={15} /></button>
                  <a href={link} target="_blank" rel="noreferrer" className="text-[var(--acc2)] shrink-0" title="Open"><ExternalLink size={15} /></a>
                </div>
                {copied && <div className="text-[11px] text-[var(--acc2)] font-semibold mt-1">Copied ✓</div>}
                <div className="text-[11px] text-[var(--muted)] mt-3">Tracking code: <span className="font-mono px-1.5 py-0.5 rounded bg-[var(--soft)]">?c={c.code}</span> — append it to any card/microsite link.</div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-[var(--muted)]">Set a <b>landing rep</b> on this campaign (Edit) to generate the shareable link + QR.</div>
          )}
        </div>

        <div className="bg-[var(--card)] rounded-3xl border border-[var(--border)] card-lux p-6">
          <h3 className="font-extrabold text-[var(--text)] text-[16px] mb-4">Goal &amp; cost</h3>
          <div className="space-y-3">
            {c.targetLeads > 0 && (
              <div>
                <div className="flex justify-between text-[12px] mb-1"><span className="text-[var(--muted)]">Leads vs target</span><span className="font-bold text-[var(--text)]">{stats.leads || 0} / {c.targetLeads}</span></div>
                <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--soft)' }}><div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.round(((stats.leads || 0) / c.targetLeads) * 100))}%`, background: 'linear-gradient(90deg,#10b981,#0b6e3e)' }} /></div>
              </div>
            )}
            <div className="flex justify-between text-[13px]"><span className="text-[var(--muted)]">Budget</span><span className="font-bold text-[var(--text)]">{inr(c.budget)}</span></div>
            <div className="flex justify-between text-[13px]"><span className="text-[var(--muted)]">Cost / lead</span><span className="font-bold text-[var(--text)]">{cpl ? inr(cpl) : '—'}</span></div>
            {(c.startDate || c.endDate) && <div className="flex justify-between text-[13px]"><span className="text-[var(--muted)]">Dates</span><span className="font-semibold text-[var(--text)]">{c.startDate || '…'} → {c.endDate || '…'}</span></div>}
          </div>
        </div>
      </div>

      {/* campaign leads */}
      <div className="flex items-center justify-between">
        <h3 className="font-extrabold text-[var(--text)] text-[17px]">Leads in this campaign ({leads.length})</h3>
        <div className="inline-flex rounded-xl p-1" style={{ background: 'var(--soft)' }}>
          {['grid', 'list'].map((v) => (
            <button key={v} onClick={() => setView(v)} className="px-4 py-1.5 rounded-lg text-[13px] font-bold capitalize transition-colors"
              style={view === v ? { background: 'var(--card)', color: 'var(--acc2)', boxShadow: '0 1px 3px rgba(0,0,0,.12)' } : { color: 'var(--muted)' }}>{v}</button>
          ))}
        </div>
      </div>
      {leads.length === 0 ? (
        <div className="bg-[var(--card)] rounded-2xl border border-[var(--border)] p-10 text-center text-[var(--muted)]">
          No leads yet. Share the tracking link/QR above, or assign leads to this campaign from a lead's page.
        </div>
      ) : view === 'grid' ? <LeadGrid leads={leads} onOpen={setOpenId} /> : <LeadLedger leads={leads} onOpen={setOpenId} />}

      {openId && <LeadDetail id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
